'use client'

import { useMemo } from 'react'

import { getSelfPaidInsurancePrice } from '@/_lib/order/template-visit-pricing'
import { formatPrice, orderFlowPriceFormatOptions } from '@/_lib/utils'
import { AMBULANCE_PRICE_LIST_TABLE_TYPE } from '@/_types/ambulance'
import { calculateDoctorAmbulancePrice } from '@/app/[locale]/order/_utils/price-calculation.utils'

import { CartProductType } from '../../../../_context/order-flow-context'

import type { ApiResponse, IOrderTimeSlotDetail } from '@/_types'
import type { IAnnualProgramDiscountData } from '@/app/[locale]/order/_types/annual-program-discount'

type UseOrderPatientSubmitCtaParams = {
	createOrderIsPending: boolean
	isCheckingDiscount: boolean
	pricesLoaded: boolean
	cartProductsLength: number
	totalAll: number
	primaryType: CartProductType | undefined
	timeslotDetail: ApiResponse<IOrderTimeSlotDetail> | undefined
	showInsurance: boolean
	watchedInsurance: string | undefined
	annualProgramDiscount: IAnnualProgramDiscountData | null
	/**
	 * Price-table type of the selected ambulance.
	 * Controls when the price is shown on the submit button:
	 *
	 * - PLUS_DEFAULT_PRICE_FOR_INSURED → price is always shown (fixed for everyone).
	 * - DEFAULT / PLUS_WITH → price is hidden until the patient picks an insurer.
	 */
	ambulancePriceListTableType: AMBULANCE_PRICE_LIST_TABLE_TYPE | null
	/**
	 * Same meaning as `templateCartPricing.showFromPrefix` in order flow: true while the
	 * cart shows the minimum estimate with an "od" prefix because no insurer is chosen yet.
	 * The aggregated cart total is then not a firm checkout price — omit it from the
	 * submit label (aligned with the cart summary).
	 */
	insuranceCompanyPending: boolean
}

/**
 * Submit button label and disabled state for the patient-info step.
 *
 * Price-display rules on the submit button:
 *
 * PLUS_DEFAULT_PRICE_FOR_INSURED
 *   Always shows the self-payer (SA00) price regardless of which insurer
 *   the patient selects, because all insurer prices are equal for this type.
 *
 * DEFAULT | PLUS_WITH_AND_WITHOUT_YEAR_PROGRAM_PRICE
 *   - No insurer selected → button shows no price ("Objednať s povinnosťou platby").
 *   - Insurer selected → shows the exact insurer price (with annual-program rules).
 *
 * When `insuranceCompanyPending` is true (cart "od" prefix), the cart total is only a
 * minimum estimate — the submit button omits any price even if `pricesLoaded` is true.
 */
export function useOrderPatientSubmitCta({
	createOrderIsPending,
	isCheckingDiscount,
	pricesLoaded,
	cartProductsLength,
	totalAll,
	primaryType,
	timeslotDetail,
	showInsurance,
	watchedInsurance,
	annualProgramDiscount,
	ambulancePriceListTableType,
	insuranceCompanyPending
}: UseOrderPatientSubmitCtaParams) {
	const priceResult = useMemo(() => {
		if (primaryType !== CartProductType.TEMPLATE) return null

		const tpl = timeslotDetail?.data?.template

		// PLUS_DEFAULT_PRICE_FOR_INSURED: fixed price for all — use the SA00 entry
		// so the button always shows a price even before insurance is chosen.
		if (
			ambulancePriceListTableType ===
			AMBULANCE_PRICE_LIST_TABLE_TYPE.PLUS_DEFAULT_PRICE_FOR_INSURED
		) {
			const fixedPrice = getSelfPaidInsurancePrice(
				tpl?.insuranceCompanies,
				tpl?.price ?? null
			)
			if (fixedPrice == null) return null
			return { price: fixedPrice, description: '', annualProgramHighlight: null }
		}

		// DEFAULT / PLUS_WITH: do not show price until the patient selects an insurer.
		if (!showInsurance || !watchedInsurance) return null

		return calculateDoctorAmbulancePrice(
			timeslotDetail,
			watchedInsurance,
			annualProgramDiscount
		)
	}, [
		ambulancePriceListTableType,
		annualProgramDiscount,
		primaryType,
		showInsurance,
		timeslotDetail,
		watchedInsurance
	])

	const submitButtonLabel = useMemo(() => {
		if (createOrderIsPending) return 'Odosielam objednávku…'
		if (isCheckingDiscount) return 'Kontrolujeme Váš ročný program…'
		if (
			!insuranceCompanyPending &&
			pricesLoaded &&
			cartProductsLength > 0
		) {
			const f = formatPrice(totalAll, orderFlowPriceFormatOptions)
			return `Objednať s povinnosťou platby za ${f.price}\u00a0${f.currency}`
		}
		if (priceResult?.price !== null && priceResult?.price !== undefined) {
			const f = formatPrice(priceResult.price, orderFlowPriceFormatOptions)
			return `Objednať s povinnosťou platby za ${f.price}\u00a0${f.currency}`
		}
		return 'Objednať s povinnosťou platby'
	}, [
		cartProductsLength,
		createOrderIsPending,
		insuranceCompanyPending,
		isCheckingDiscount,
		priceResult,
		pricesLoaded,
		totalAll
	])

	const submitDisabled = createOrderIsPending || isCheckingDiscount

	return { submitButtonLabel, submitDisabled, priceResult }
}
